import { useCallback, useEffect, useState } from "react";
import { deleteModel, getModelById, getMetadataDependency } from "../../services/DataModelService";
import ModelList from "../../components/configuration/ModelList";
import { useDisclosure } from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { MetadataTypeEnum } from "build-ts/enums";
import { DependencyRequestProto } from "build-ts/interface";
import { useNavigate } from "react-router-dom";
import { useConfigurationContext } from "context/ConfigurationContext";

interface ModelListContainerProps {
  modelList: Header[];
  setModelList: React.Dispatch<React.SetStateAction<Header[]>>;
}

const ModelListContainer: React.FC<ModelListContainerProps> = ({ modelList, setModelList }) => {
  const navigate = useNavigate();
  const {
    attributeKgIds, measureKgIds, temporalKgIds,
    modelInfoMap, setModelInfoMap, dependencyMap, setDependencyMap,
  } = useConfigurationContext();
  const [deleteModelId, setDeleteModelId] = useState<string | null>(null);
  const [dependentMetricHeaders, setDependentMetricHeaders] = useState<Header[]>([]);
  const deleteModal = useDisclosure();

  const [loading, setLoading] = useState(() => {
    // Start as loading only if there are models we haven't cached yet
    return modelList.some((m) => m.id && !modelInfoMap.has(m.id));
  });

  const loadData = useCallback(async () => {
    // Only fetch models that are not already in the cache
    const uncachedIds = modelList
      .map((m) => m.id)
      .filter((id): id is string => Boolean(id))
      .filter((id) => !modelInfoMap.has(id));

    if (uncachedIds.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.all(
        uncachedIds.map(async (modelId) => {
          // Step 1: fetch ModelInfo for column list and worksheet id
          const modelInfo = await getModelById(modelId);
          if (!modelInfo) return { modelId, modelInfo: null, depMap: new Map<string, Header[]>() };

          // Step 2: fetch dependencies for all logical columns in this model
          const columnIds = modelInfo.columns
            .map((c) => c.header?.id)
            .filter((id): id is string => Boolean(id));

          const depMap = new Map<string, Header[]>();
          if (columnIds.length > 0) {
            const request = DependencyRequestProto.create({
              ids: columnIds,
              metadata_type: MetadataTypeEnum.LOGICAL_COLUMN,
              dependent_types: [
                MetadataTypeEnum.KG_COLUMN,
                MetadataTypeEnum.METRIC,
              ],
              ignore_definition_filters: true,
            });
            const deps = await getMetadataDependency(request);
            if (deps) {
              for (const dep of deps) {
                if (dep.metadata_id && dep.headers.length > 0) {
                  depMap.set(dep.metadata_id, dep.headers);
                }
              }
            }
          }

          return { modelId, modelInfo, depMap };
        })
      );

      // Merge fetched entries into the existing context maps
      setModelInfoMap((prev) => {
        const next = new Map(prev);
        for (const { modelId, modelInfo } of results) {
          if (modelInfo) next.set(modelId, modelInfo);
        }
        return next;
      });

      setDependencyMap((prev) => {
        const next = new Map(prev);
        for (const { modelId, depMap } of results) {
          next.set(modelId, depMap);
        }
        return next;
      });
    } finally {
      setLoading(false);
    }
  }, [modelList, modelInfoMap, setModelInfoMap, setDependencyMap]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleOpenModelEditor = (id: string) => {
    navigate(`/mkg/models/${id}`);
  };

  const handleOpenThoughtspotFormula = useCallback(
    (modelId: string) => {
      const ws = modelInfoMap.get(modelId)?.data_source?.ts_worksheet_id;
      if (ws) navigate(`/mkg/thoughtspot/${ws}`);
    },
    [modelInfoMap, navigate]
  );

  const handleDeleteModel = async (id: string) => {
    const response = await deleteModel(id);
    if (response.length > 0) {
      deleteModal.onOpen();
      setDependentMetricHeaders(response);
    } else {
      setModelList((prev) => prev.filter((model) => model.id !== id));
    }
  };

  const confirmDelete = () => {
    if (deleteModelId) {
      handleDeleteModel(deleteModelId);
      setDeleteModelId(null);
    }
    deleteModal.onClose();
  };

  return (
    <ModelList
      listModel={modelList}
      handleOpenModelEditor={handleOpenModelEditor}
      onOpenThoughtspotFormula={handleOpenThoughtspotFormula}
      deleteModal={deleteModal}
      confirmDelete={confirmDelete}
      setDeleteModelId={setDeleteModelId}
      dependentMetricHeaders={dependentMetricHeaders}
      setDependentMetricHeaders={setDependentMetricHeaders}
      modelInfoMap={modelInfoMap}
      dependencyMap={dependencyMap}
      loading={loading}
      attributeKgIds={attributeKgIds}
      measureKgIds={measureKgIds}
      temporalKgIds={temporalKgIds}
    />
  );
};

export default ModelListContainer;
